/**
 * Offline-aware wrapper around the shared axios instance.
 *
 *  - For GET /cases and GET /cases/{id}: hits the network first, mirrors the
 *    response to IndexedDB, and falls back to IndexedDB when offline.
 *  - For POST/PUT/DELETE /cases and /cases/{id}: hits the network first; on
 *    network failure it queues the mutation in the outbox and returns an
 *    optimistic response so the UI can proceed. The outbox is drained
 *    whenever the browser regains connectivity (or on explicit `syncOutbox`).
 *
 * We intentionally scope offline WRITES to /cases only — auth, staff/profile
 * mutations happen rarely and should require connectivity.
 */
import api from "@/lib/api";
import {
  putCases,
  putCase,
  getAllCases,
  getCase as getCaseLocal,
  deleteCaseLocal,
  enqueue,
  getPending,
  removeOutbox,
  updateOutbox,
  countPending,
} from "@/lib/offlineDB";

function isNetworkError(err) {
  // axios: no response = network failure (offline, CORS, DNS, etc.)
  if (!err) return false;
  if (err.code === "ERR_NETWORK") return true;
  if (err.message === "Network Error") return true;
  return !err.response && !!err.request;
}

function tempId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

let _syncing = false;
let _detectedOffline = false;
const _listeners = new Set();

function notify() {
  _listeners.forEach((cb) => {
    try {
      cb();
    } catch (_) {
      /* ignore */
    }
  });
}

export function onOfflineChange(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

export function isDetectedOffline() {
  return _detectedOffline;
}

function markOffline() {
  if (_detectedOffline) return;
  _detectedOffline = true;
  notify();
}

function markOnline() {
  if (!_detectedOffline) return;
  _detectedOffline = false;
  notify();
}

export async function getPendingCount() {
  try {
    return await countPending();
  } catch (_) {
    return 0;
  }
}

/* ----------------------------- reads -------------------------------------- */
async function _localPendingCases() {
  try {
    const all = await getAllCases();
    return all.filter((c) => c && c._pending);
  } catch (_) {
    return [];
  }
}

function _mergeUnique(serverCases, localPending) {
  const seen = new Set(serverCases.map((c) => c.id));
  const extras = localPending.filter((c) => !seen.has(c.id));
  return [...extras, ...serverCases];
}

export async function listCases(params = {}) {
  const localPending = await _localPendingCases();
  try {
    const { data } = await api.get("/cases", { params });
    markOnline();
    const arr = Array.isArray(data) ? data : [];
    // Mirror the server list back to IDB (best-effort).
    putCases(arr).catch(() => {});
    // Merge any still-unsynced optimistic pending cases so they appear in the
    // list even when the SW served /api/cases from stale cache.
    return filterLocal(_mergeUnique(arr, localPending), params);
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    markOffline();
    const all = await getAllCases();
    return filterLocal(all, params);
  }
}

function filterLocal(cases, params) {
  const {
    search = "",
    role = "",
    attending = "",
    category = "",
    omfs_category = "",
    month = "",
  } = params;
  const q = (search || "").toLowerCase();
  return cases
    .filter((c) => {
      if (q) {
        const blob = `${c.procedure_name || ""} ${c.attending_physician || ""} ${c.institution || ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (role && c.role_in_case !== role) return false;
      if (attending && c.attending_physician !== attending) return false;
      if (category && c.procedure_category !== category) return false;
      if (omfs_category && c.omfs_procedure_category !== omfs_category) return false;
      if (month && !(c.date_of_procedure || "").startsWith(month)) return false;
      return true;
    })
    .sort((a, b) =>
      (b.date_of_procedure || "").localeCompare(a.date_of_procedure || ""),
    );
}

export async function getCase(id) {
  try {
    const { data } = await api.get(`/cases/${id}`);
    if (data) putCase(data).catch(() => {});
    return data;
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    const local = await getCaseLocal(id);
    if (!local) throw err;
    return local;
  }
}

/* ---------------------------- mutations ----------------------------------- */
export async function createCase(payload) {
  try {
    const { data } = await api.post("/cases", payload);
    markOnline();
    if (data) putCase(data).catch(() => {});
    return data;
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    markOffline();
    // Optimistically build a case object; the SERVER id will overwrite this
    // during outbox replay.
    const optimistic = {
      ...payload,
      id: tempId(),
      _pending: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    await putCase(optimistic);
    await enqueue({
      op: "create",
      payload,
      localId: optimistic.id,
    });
    notify();
    return optimistic;
  }
}

export async function updateCase(id, payload) {
  try {
    const { data } = await api.put(`/cases/${id}`, payload);
    markOnline();
    if (data) putCase(data).catch(() => {});
    return data;
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    markOffline();
    const existing = (await getCaseLocal(id)) || {};
    const merged = {
      ...existing,
      ...payload,
      id,
      _pending: true,
      updated_at: new Date().toISOString(),
    };
    await putCase(merged);
    await enqueue({ op: "update", id, payload });
    notify();
    return merged;
  }
}

export async function deleteCase(id) {
  try {
    await api.delete(`/cases/${id}`);
    markOnline();
    await deleteCaseLocal(id);
    return { ok: true };
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    markOffline();
    // If id is a local temp id, try to remove any pending create for it and
    // just drop from the mirror.
    if (id.startsWith("local-")) {
      const pending = await getPending();
      const p = pending.find((m) => m.localId === id);
      if (p) await removeOutbox(p.id);
      await deleteCaseLocal(id);
      notify();
      return { ok: true, pending: true };
    }
    await deleteCaseLocal(id);
    await enqueue({ op: "delete", id });
    notify();
    return { ok: true, pending: true };
  }
}

/* ------------------------------- sync ------------------------------------- */
export async function syncOutbox() {
  if (_syncing) return { synced: 0, failed: 0, alreadyRunning: true };
  _syncing = true;
  let synced = 0;
  let failed = 0;
  try {
    const pending = await getPending();
    for (const mut of pending) {
      try {
        if (mut.op === "create") {
          const { data } = await api.post("/cases", mut.payload);
          if (mut.localId) {
            await deleteCaseLocal(mut.localId);
          }
          if (data) await putCase(data);
        } else if (mut.op === "update") {
          const { data } = await api.put(`/cases/${mut.id}`, mut.payload);
          if (data) await putCase(data);
        } else if (mut.op === "delete") {
          await api.delete(`/cases/${mut.id}`);
          await deleteCaseLocal(mut.id);
        }
        await removeOutbox(mut.id);
        markOnline();
        synced += 1;
      } catch (err) {
        failed += 1;
        if (isNetworkError(err)) {
          markOffline();
          break; // stop draining until reconnected
        }
        // Non-network error (server rejected the write) — mark and skip.
        await updateOutbox(mut.id, {
          status: "failed",
          attempts: (mut.attempts || 0) + 1,
          lastError:
            err?.response?.data?.detail
              ? JSON.stringify(err.response.data.detail).slice(0, 300)
              : err?.message || "unknown",
        });
      }
    }
  } finally {
    _syncing = false;
    notify();
  }
  return { synced, failed };
}

export function attachAutoSync() {
  if (typeof window === "undefined") return () => {};
  const handler = () => {
    if (navigator.onLine) syncOutbox();
  };
  window.addEventListener("online", handler);
  // First-load: try once (in case there's a leftover queue from a previous session).
  if (navigator.onLine) setTimeout(() => syncOutbox(), 1500);
  return () => window.removeEventListener("online", handler);
}
